import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { SafePipe } from '../../pipes/safe.pipe';

import { FlimmoMetaService } from '../../../core/services/flimmo-meta.service';
import { SubSink } from 'subsink';
import { PreviewItem, Source, Swimlane } from '../../../generated-api';
import { formatDate } from '../../functions/utils';
import * as Constants from '../../../core/helpers/constants/constants';
import { DefaultLayoutDirective, DefaultFlexDirective, DefaultLayoutAlignDirective, DefaultLayoutGapDirective } from '@ngbracket/ngx-layout/flex';
import { AgeCategoryBadgeComponent } from '../age-category-badge/age-category-badge.component';
import { TrafficLightsComponent } from '../traffic-lights/traffic-lights.component';
import { TranslateModule } from '@ngx-translate/core';
import ItemclassEnum = PreviewItem.ItemclassEnum;

@Component({
  selector: 'verticallanes',
  templateUrl: './verticallanes.component.html',
  styleUrls: ['./verticallanes.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  //imports: [CommonModule, SafePipe, RouterModule],
  host: { ngSkipHydration: 'true' },
  imports: [
    DefaultLayoutDirective,
    DefaultFlexDirective,
    DefaultLayoutAlignDirective,
    DefaultLayoutGapDirective,
    AgeCategoryBadgeComponent,
    TrafficLightsComponent,
    TranslateModule,
    SafePipe,
  ],
})
export class VerticallanesComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private _swimlane: Swimlane;
  public get swimlane(): Swimlane {
    return this._swimlane;
  }

  @Input()
  public set swimlane(value: Swimlane) {
    this._swimlane = value;
    this.swimlaneLinks = value.items.map((item) => this.generatelinks(item));
  }

  @Input() isTopListeTopListe = false;

  public getKachelText(id: number, fallback: string): string {
    if (!this.isTopListeTopListe) {
      return fallback;
    }
    switch (id) {
      case 101591: return 'Aufregende Abenteuer, etwas Magie und jede Menge Action – diese Kinderfilme sorgen für Nervenkitzel. Sie sind spannend, aber kindgerecht. Denn im Grundschulalter darf es für viele Kinder schon etwas rasanter zugehen. Falls einzelne Szenen jüngere oder empfindlichere Kinder überfordern könnten, wird darauf hingewiesen.';
      case 101603: return 'Filmklassiker bieten Kindern zeitlose Geschichten, die auch heute noch faszinieren können. Die Auswahl umfasst empfehlenswerte Highlights aus vergangenen Jahrzehnten, die Kinder und Eltern am Besten gemeinsam (wieder)erleben.';
      case 101592: return 'Diese Serien sind auch schon für die Jüngsten gut geeignet. Sie sind ruhig und einfach gestaltet, also "reizarm" und greifen Themen aus dem Alltag der Kinder auf. Grundsätzlich gilt: Kinder unter 3 Jahren sollten noch möglichst wenig Zeit vor dem Bildschirm verbringen. Als Ausnahme sind kleine Häppchen möglich.';
      case 101590: return 'Kindern macht es besonders viel Spaß, gemeinsam einen Film zu schauen. Oft ist die Filmauswahl gar nicht so leicht: Die Großen sollen sich nicht langweilen und die Kleinen nicht überfordert werden. FLIMMO hat hier Filme ausgewählt, die bei Kindern unterschiedlichen Alters ankommen.';
      case 101595: return 'Kinder sind neugierig und haben viele Fragen. Ab dem Grundschulalter interessieren sie sich zunehmend für komplexere Themen. Hier wird Wissen anschaulich und kindgerecht vermittelt.';
      case 101605: return 'Kurzfilme bieten Kindern kleine, besondere Geschichten für zwischendurch. Diese Auswahl vereint liebevoll erzählte, altersgerechte Filme, die zum Lachen, Staunen und Mitfühlen einladen – abwechslungsreich und kreativ. Besonders für kleinere Kinder sind Kurzfilme gut geeignet.';
      case 101604: return 'Ob Eisbären in der Arktis oder Füchse im Wald: Tiere faszinieren viele Kinder. Diese Filme und Dokumentationen bieten spannende Einblicke in das Leben von Tieren und vermitteln Wissen auf kindgerechte, anschauliche Weise.';
      default: return fallback;
    }
  }

  swimlaneLinks: string[];
  @Input() loading = false;
  imageLoaded: Record<number, boolean> = {};
  @Input() bigCards = false;
  @ViewChild('container') container: ElementRef;
  @Input() tvLane = false;

  sic = ItemclassEnum;
  subs = new SubSink();
  currentDate = formatDate(new Date());
  private leftAtLimit = true;
  private rightAtLimit = false;
  private cardWidth = 160;
  private gap = 15;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.cr.detectChanges();
    this.cr.markForCheck();
  }

  readonly PLACEHOLDER_IMAGE_WITH_PATH = Constants.PLACEHOLDER_IMAGE_WITH_PATH;

  constructor(
    public flimmoMeta: FlimmoMetaService,
    private cr: ChangeDetectorRef,
    private router: Router
  ) { }

  ngAfterViewInit(): void {
    this.cr.detectChanges();
    this.cr.markForCheck();
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.cr.detectChanges();
    this.cr.markForCheck();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  open(url: string): void {
    this.router.navigateByUrl(url);
  }

  generatelinks(m: PreviewItem): string {
    if (m.itemclass === this.sic.Besprechung) {
      return `/besprechung/${m.id}/${this.flimmoMeta.prettifyUrl(m.title)}`;
    } else if (m.itemclass === this.sic.Ytbesprechung) {
      return `/ytbesprechung/${m.id}/${this.flimmoMeta.prettifyUrl(m.title)}`;
    } else if (m.itemclass === this.sic.Redtext) {
      let title = m.title
      if (!title && m.topos) {
        title = m.topos
      }
      return `/redtext/${m.id}/${this.flimmoMeta.prettifyUrl(title)}`;
    } else if (m.itemclass === this.sic.Special) {
      return `/special/${m.id}/${this.flimmoMeta.prettifyUrl(m.title)}`;
    }
    return `/`;
  }

  onImageError(event): void {
    event.target.src = Constants.PLACEHOLDER_IMAGE_WITH_PATH;
  }

  getText(srclist: Array<Source>): string {
    return srclist
      .sort(
        (a, b) =>
          this.srcSequence.indexOf(a.src) - this.srcSequence.indexOf(b.src)
      )
      .map((value) => value.srcName)
      .join(', ');
  }

  hasSrc(m: PreviewItem): boolean {
    return (
      (m.itemclass === this.sic.Besprechung ||
        m.itemclass === this.sic.Ytbesprechung) &&
      m.srclist &&
      m.srclist.length > 0
    );
  }

  getAlterText(ageCategory): string {
    switch (ageCategory) {
      case 3:
        return 'searchList.selectors.ageOptionsAlt.3';
      case 5:
        return 'searchList.selectors.ageOptionsAlt.5';
      case 7:
        return 'searchList.selectors.ageOptionsAlt.7';
      case 9:
        return 'searchList.selectors.ageOptionsAlt.9';
      case 11:
        return 'searchList.selectors.ageOptionsAlt.11';
    }
  }


  srcSequence = [
    'kino',
    'netflix',
    'amazon',
    'disney_plus',
    'kika',
    'toggo',
    'ARD Mediathek',
    'ZDF Mediathek',
    'RTLPLUS',
    'Joyn',
    'joyn',
    'filmfriend',
    'youtube',
    'youtubekids',
    'tiktok',
    'instagram',
    'tv',
    'KKA',
    'RTLS',
    'Disney Channel Free TV',
    'NICK',
    'RTL',
    'RTL2',
    'PRO7',
    'SAT1',
    'K1',
    'ARD',
    'ZDF',
  ];

}
